import React, { useEffect, useRef, useState } from 'react';
import InfoTooltip from './InfoTooltip';

const API_BASE = process.env.REACT_APP_API_URL || 'https://norwich-hub.onrender.com';

interface OracleProfile {
  id:          string;
  name:        string;
  icon:        string;
  description: string;
  credentials: string;
  color:       string;
}

interface Outcome { id: string; label: string; }

interface Props {
  marketId:    string;
  outcomes:    Outcome[];
  currentUser: string;
  onVibeSubmitted?: (outcomeId: string, compositeVibe: number) => void;
}

function ProfileCard({ oracle }: { oracle: OracleProfile }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={oracle.name}
        style={{
          display:        'flex',
          alignItems:     'center',
          gap:            6,
          padding:        '5px 10px',
          borderRadius:   6,
          border:         `1px solid ${open ? oracle.color : '#334155'}`,
          backgroundColor: open ? `${oracle.color}22` : '#1e293b',
          color:          open ? oracle.color : '#94a3b8',
          cursor:         'pointer',
          fontFamily:     'monospace',
          fontSize:       10,
          letterSpacing:  '0.08em',
          transition:     'all 0.15s',
          whiteSpace:     'nowrap',
        }}
      >
        <span style={{ fontSize: 13 }}>{oracle.icon}</span>
        {oracle.name}
        <span style={{ color: open ? oracle.color : '#475569', fontSize: 8 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position:        'absolute',
          top:             'calc(100% + 6px)',
          left:            0,
          zIndex:          50,
          backgroundColor: '#0f172a',
          border:          `1px solid ${oracle.color}55`,
          borderTop:       `2px solid ${oracle.color}`,
          borderRadius:    8,
          padding:         '12px 14px',
          width:           260,
          boxShadow:       `0 0 20px ${oracle.color}22`,
          fontFamily:      'monospace',
        }}>
          <p style={{ color: oracle.color, fontSize: 10, letterSpacing: '0.14em', margin: '0 0 8px' }}>
            {oracle.icon} {oracle.name.toUpperCase()}
          </p>
          <p style={{ color: '#94a3b8', fontSize: 11, lineHeight: 1.6, margin: '0 0 10px' }}>
            {oracle.description}
          </p>
          <div style={{ borderTop: '1px solid #1e293b', paddingTop: 8 }}>
            <p style={{ color: '#475569', fontSize: 8, letterSpacing: '0.14em', margin: '0 0 4px' }}>
              CREDENTIALS
            </p>
            <p style={{ color: '#64748b', fontSize: 10, lineHeight: 1.5, margin: 0 }}>
              {oracle.credentials}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OraclePanel({ marketId, outcomes, currentUser, onVibeSubmitted }: Props): React.JSX.Element {
  const [profiles,    setProfiles]    = useState<OracleProfile[]>([]);
  const [selOutcome,  setSelOutcome]  = useState('');
  const [vibeVal,     setVibeVal]     = useState('0.5');
  const [reasoning,   setReasoning]   = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [result,      setResult]      = useState<{ text: string; ok: boolean } | null>(null);

  // Identity gate — MultiPage passes `identity.id ?? ''`, so an empty
  // string means the wallet is disconnected / the visitor is Tier 0
  // with no Ghost id yet. Oracle submissions require a user_id, so
  // treat that as a hard gate on the form rather than letting the
  // request fire and be rejected by the backend as a missing field.
  const userId    = (currentUser ?? '').trim();
  const connected = userId.length > 0;

  useEffect(() => {
    fetch(`${API_BASE}/api/oracles`)
      .then(r => r.json() as Promise<OracleProfile[]>)
      .then(data => setProfiles(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (outcomes.length > 0 && !selOutcome) setSelOutcome(outcomes[0].id);
  }, [outcomes, selOutcome]);

  async function submit(): Promise<void> {
    // Payload guard — refuse to fire unless every required field is
    // present. Previously the POST would still go out with an empty
    // user_id and the UI would surface the raw backend message
    // ("market_id, outcome_id, user_id, vibe are required"), which is
    // both ugly and actionable-only-if-you-know-the-schema.
    if (!connected) {
      setResult({ text: 'Connect your wallet to submit a signal.', ok: false });
      return;
    }
    if (!marketId) {
      setResult({ text: 'No market selected — try again once the market loads.', ok: false });
      return;
    }
    if (!selOutcome) {
      setResult({ text: 'Pick an outcome before submitting a vibe.', ok: false });
      return;
    }
    const vibe = parseFloat(vibeVal);
    if (isNaN(vibe) || vibe < 0 || vibe > 1) {
      setResult({ text: 'Vibe must be between 0.0 and 1.0', ok: false });
      return;
    }

    setSubmitting(true);
    setResult(null);

    // v2.2.8 — Identity sync ladder. Observer citizen_XXX ids are
    // minted client-side (localStorage) before the backend has ever
    // heard of them, so the first oracle_submit can 404 with
    // "Unknown user". On that specific failure we silently register
    // the persona and retry once. Button stays in SUBMITTING… the
    // whole time so the user can't double-fire.
    async function postVibe() {
      return fetch(`${API_BASE}/api/multi/oracle_submit`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          market_id:  marketId,
          outcome_id: selOutcome,
          user_id:    userId,
          vibe,
          reasoning,
        }),
      });
    }

    async function ensureRegistered(): Promise<boolean> {
      try {
        const r = await fetch(`${API_BASE}/api/register_persona`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ user_id: userId }),
        });
        return r.ok;
      } catch { return false; }
    }

    try {
      let res = await postVibe();
      let data: { error?: string; composite_vibe?: number; your_weight?: number } = {};
      try { data = await res.json(); } catch { /* non-JSON response */ }

      if (!res.ok && (res.status === 404 || /unknown user/i.test(data.error ?? ''))) {
        // Silent Registration + retry once.
        const registered = await ensureRegistered();
        if (registered) {
          res  = await postVibe();
          data = {};
          try { data = await res.json(); } catch { /* non-JSON */ }
        }
      }

      if (!res.ok) {
        // Keep the developer trail, but show the user a clean line —
        // never a raw SQL/schema message.
        // eslint-disable-next-line no-console
        console.error('[OraclePanel] submit failed', { status: res.status, body: data });
        const raw = data.error ?? '';
        const clean = /required|missing|not-null|constraint/i.test(raw)
          ? 'Could not submit signal — please reconnect and try again.'
          : raw || `Submission failed (HTTP ${res.status}).`;
        setResult({ text: clean, ok: false });
      } else if (typeof data.composite_vibe === 'number') {
        setResult({
          text: `Submitted. New composite vibe: ${(data.composite_vibe * 100).toFixed(1)}%` +
                (typeof data.your_weight === 'number' ? ` (weight: ${data.your_weight}×)` : ''),
          ok:   true,
        });
        setReasoning('');
        onVibeSubmitted?.(selOutcome, data.composite_vibe);
      } else {
        setResult({ text: 'Submission accepted, but the response was empty.', ok: true });
      }
    } catch (e) {
      // Network / CORS / offline — keep the developer trail, show a
      // short, non-technical line to the user.
      // eslint-disable-next-line no-console
      console.error('[OraclePanel] submit threw', e);
      setResult({ text: 'Network error — please check your connection and retry.', ok: false });
    } finally {
      setSubmitting(false);
    }
  }

  const vibeNum = parseFloat(vibeVal) || 0;
  const vibeColor = vibeNum >= 0.6 ? '#34d399' : vibeNum >= 0.4 ? '#fbbf24' : '#f87171';

  return (
    <div style={{
      backgroundColor: '#0f172a',
      border:          '1px solid #1e293b',
      borderRadius:    8,
      padding:         '14px 16px',
      fontFamily:      'monospace',
    }}>
      <p style={{ color: '#475569', fontSize: 9, letterSpacing: '0.18em', margin: '0 0 12px' }}>
        ORACLE PROFILES
      </p>

      {/* Profile cards with tooltips. The row has a reserved min-height
          so the "Loading oracles…" placeholder (position: absolute) no
          longer pushes downstream content around — the phantom flash in
          the bottom-left of the column was this element reflowing every
          time the profile list briefly emptied during a refetch. */}
      <div style={{
        position:   'relative',
        display:    'flex',
        flexWrap:   'wrap',
        gap:        6,
        marginBottom: 16,
        minHeight:  44,
      }}>
        {profiles.map(o => <ProfileCard key={o.id} oracle={o} />)}
        {profiles.length === 0 && (
          <span style={{
            position:       'absolute',
            left:           0,
            top:            '50%',
            transform:      'translateY(-50%)',
            color:          '#334155',
            fontSize:       10,
            pointerEvents:  'none',
          }}>Loading oracles…</span>
        )}
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid #1e293b', marginBottom: 14 }} />

      {/* Community Oracle submission */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 12 }}>
        <p style={{ color: '#f59e0b', fontSize: 9, letterSpacing: '0.16em', margin: 0 }}>
          🧠 YOUR ORACLE CONTRIBUTION <span style={{ color: '#78350f' }}>·</span>{' '}
          <span style={{ color: '#92400e' }}>
            {connected ? userId.toUpperCase() : 'NO IDENTITY'}
          </span>
        </p>
        <InfoTooltip
          title="Oracle Contribution"
          body="Your vibe score shapes the community composite. Written reasoning earns you The Oracle title and increases your weight (DP-based) in future composites — making your submissions move the Aura ring further."
          accent="#f59e0b"
          direction="below"
        />
      </div>

      {/* Identity guardrail — when disconnected, every control below is
          disabled and this notice stands in for the submit button. The
          form remains visible so the shape of the contribution surface
          is obvious before connect, but no POST can be fired without a
          user_id. */}
      {!connected && (
        <div style={{
          backgroundColor: '#1c1408',
          border:          '1px solid #78350f',
          borderLeft:      '2px solid #fcd34d',
          borderRadius:    5,
          padding:         '8px 10px',
          color:           '#fcd34d',
          fontSize:        10,
          lineHeight:      1.5,
          letterSpacing:   '0.04em',
          marginBottom:    12,
        }}>
          Connect to submit signal/vibe.
        </div>
      )}

      {/* Outcome selector */}
      <label style={{ display: 'block', marginBottom: 10 }}>
        <span style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.14em', display: 'block', marginBottom: 5 }}>
          OUTCOME
        </span>
        <select
          value={selOutcome}
          onChange={e => setSelOutcome(e.target.value)}
          disabled={!connected || outcomes.length === 0}
          style={{
            width: '100%', backgroundColor: '#1e293b', border: '1px solid #334155',
            borderRadius: 5, color: '#e2e8f0', fontSize: 11, padding: '6px 10px',
            fontFamily: 'monospace', outline: 'none',
            cursor: connected && outcomes.length > 0 ? 'pointer' : 'not-allowed',
            opacity: connected ? 1 : 0.55,
          }}
        >
          {outcomes.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </label>

      {/* Vibe slider + numeric input */}
      <label style={{ display: 'block', marginBottom: 10, opacity: connected ? 1 : 0.55 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.14em' }}>VIBE SCORE</span>
          <span style={{ color: vibeColor, fontSize: 11, fontWeight: 700 }}>
            {vibeNum.toFixed(2)}  {vibeNum >= 0.6 ? '● Bullish' : vibeNum >= 0.4 ? '● Neutral' : '● Bearish'}
          </span>
        </div>
        <input
          type="range" min="0" max="1" step="0.01"
          value={vibeVal}
          onChange={e => setVibeVal(e.target.value)}
          disabled={!connected}
          style={{
            width: '100%', accentColor: vibeColor,
            cursor: connected ? 'pointer' : 'not-allowed',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
          <span style={{ color: '#f87171', fontSize: 8 }}>0.0 Bearish</span>
          <span style={{ color: '#fbbf24', fontSize: 8 }}>0.5 Neutral</span>
          <span style={{ color: '#34d399', fontSize: 8 }}>1.0 Bullish</span>
        </div>
      </label>

      {/* Reasoning textarea */}
      <label style={{ display: 'block', marginBottom: 12 }}>
        <span style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.14em', display: 'block', marginBottom: 5 }}>
          REASONING <span style={{ color: '#334155' }}>(optional)</span>
        </span>
        <textarea
          value={reasoning}
          onChange={e => setReasoning(e.target.value)}
          disabled={!connected}
          placeholder={connected
            ? 'A fortune-telling parrot told me.'
            : 'Connect to submit signal/vibe.'}
          rows={2}
          style={{
            width: '100%', backgroundColor: '#1e293b', border: '1px solid #334155',
            borderRadius: 5, color: '#e2e8f0', fontSize: 11, padding: '7px 10px',
            fontFamily: 'monospace', resize: 'vertical', outline: 'none',
            boxSizing: 'border-box',
            cursor: connected ? 'text' : 'not-allowed',
            opacity: connected ? 1 : 0.55,
          }}
        />
      </label>

      <button
        onClick={submit}
        disabled={submitting || !connected}
        title={!connected ? 'Connect to submit signal/vibe.' : undefined}
        style={{
          width: '100%', padding: '8px', borderRadius: 6,
          border:           '1px solid #78350f',
          backgroundColor: submitting || !connected ? '#1c0d00' : '#451a03',
          color:           submitting || !connected ? '#78350f' : '#fcd34d',
          fontSize: 10, letterSpacing: '0.12em',
          cursor: submitting || !connected ? 'not-allowed' : 'pointer',
          fontFamily: 'monospace',
        }}
      >
        {submitting
          ? 'SUBMITTING…'
          : !connected
            ? 'CONNECT TO SUBMIT SIGNAL/VIBE'
            : `SUBMIT VIBE AS ${userId.toUpperCase()}`}
      </button>

      {result && (
        <p style={{ marginTop: 8, fontSize: 10, lineHeight: 1.5,
          color: result.ok ? 'rgba(134,239,172,0.85)' : 'rgba(239,68,68,0.8)' }}>
          {result.text}
        </p>
      )}
    </div>
  );
}
